import React, { useState, useEffect } from 'react';
import { Users, Clock, Activity, RefreshCw, CheckCircle, XCircle, Calendar, AlertTriangle, Globe } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const UserActivitySettings = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [userStats, setUserStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [activeView, setActiveView] = useState('stats'); // 'stats' veya 'activities'

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setRefreshing(true);
      const [statsRes, activityRes] = await Promise.all([
        api.get('/admin/user-stats'),
        api.get('/admin/user-activity?limit=50')
      ]);
      setUserStats(statsRes.data);
      setActivities(activityRes.data);
    } catch (error) {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Hiç giriş yapmadı';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Az önce';
    if (diffMins < 60) return `${diffMins} dakika önce`;
    if (diffHours < 24) return `${diffHours} saat önce`;
    if (diffDays < 7) return `${diffDays} gün önce`;
    
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (user) => {
    if (user.logged_in_today) {
      return <span className="badge badge-success" style={{ fontSize: '0.7rem' }}>Bugün Aktif</span>;
    }
    
    if (!user.last_login) {
      return <span className="badge badge-secondary" style={{ fontSize: '0.7rem' }}>Hiç Giriş Yapmadı</span>;
    }

    const lastLogin = new Date(user.last_login);
    const now = new Date();
    const diffDays = Math.floor((now - lastLogin) / 86400000);

    if (diffDays <= 7) {
      return <span className="badge badge-info" style={{ fontSize: '0.7rem' }}>Bu Hafta Aktif</span>;
    }
    if (diffDays <= 30) {
      return <span className="badge badge-warning" style={{ fontSize: '0.7rem' }}>Bu Ay Aktif</span>;
    }
    return <span className="badge badge-danger" style={{ fontSize: '0.7rem' }}>Pasif ({diffDays} gün)</span>;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <RefreshCw className="spinning" size={32} />
        <p>Yükleniyor...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Başlık ve Yenile Butonu */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Activity size={20} />
          Kullanıcı Aktivitesi
        </h3>
        <button 
          className="btn btn-secondary" 
          onClick={loadData}
          disabled={refreshing}
          style={{ padding: '0.5rem 1rem' }}
        >
          <RefreshCw size={16} className={refreshing ? 'spinning' : ''} />
          Yenile
        </button>
      </div>

      {/* Özet Kartları */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <div style={{ background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', color: 'white', padding: '1.25rem', borderRadius: '12px' }}>
          <div style={{ fontSize: '2rem', fontWeight: 700 }}>{userStats?.total_users || 0}</div>
          <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Toplam Kullanıcı</div>
        </div>
        <div style={{ background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', padding: '1.25rem', borderRadius: '12px' }}>
          <div style={{ fontSize: '2rem', fontWeight: 700 }}>{userStats?.active_today || 0}</div>
          <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Bugün Aktif</div>
        </div>
        <div style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: 'white', padding: '1.25rem', borderRadius: '12px' }}>
          <div style={{ fontSize: '2rem', fontWeight: 700 }}>{activities.length}</div>
          <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Son 50 Aktivite</div>
        </div>
      </div>

      {/* Tab Butonları */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        <button
          className={`btn ${activeView === 'stats' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveView('stats')}
          style={{ padding: '0.5rem 1rem' }}
        >
          <Users size={16} style={{ marginRight: '0.25rem' }} />
          Kullanıcı Listesi
        </button>
        <button
          className={`btn ${activeView === 'activities' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveView('activities')}
          style={{ padding: '0.5rem 1rem' }}
        >
          <Clock size={16} style={{ marginRight: '0.25rem' }} />
          Son Aktiviteler
        </button>
      </div>

      {/* Kullanıcı Listesi */}
      {activeView === 'stats' && (
        <div style={{ background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>Kullanıcı</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>Rol</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>Son Giriş</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>IP Adresi</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'center', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>Toplam Giriş</th>
                <th style={{ padding: '0.75rem 1rem', textAlign: 'center', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>Durum</th>
              </tr>
            </thead>
            <tbody>
              {userStats?.users?.map((user, index) => (
                <tr key={user.user_id || index} style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: user.last_activity_suspicious ? '#fef2f2' : 'white' }}>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <div style={{ fontWeight: 500 }}>{user.user_name}</div>
                    <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{user.user_email}</div>
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <span className={`badge ${user.role === 'admin' ? 'badge-primary' : 'badge-secondary'}`}>
                      {user.role === 'admin' ? 'Admin' : 'Kullanıcı'}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Clock size={14} color="#64748b" />
                      {formatDate(user.last_login)}
                    </div>
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <code style={{ 
                        background: user.last_activity_suspicious ? '#fee2e2' : '#f1f5f9', 
                        padding: '0.25rem 0.5rem', 
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        color: user.last_activity_suspicious ? '#dc2626' : '#475569'
                      }}>
                        {user.last_ip || user.last_activity_ip || '-'}
                      </code>
                      {user.last_activity_suspicious && (
                        <span title="Şüpheli giriş - Yeni IP adresi" style={{ color: '#dc2626' }}>⚠️</span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                    <span style={{ 
                      background: '#eff6ff', 
                      color: '#3b82f6', 
                      padding: '0.25rem 0.75rem', 
                      borderRadius: '999px',
                      fontWeight: 600,
                      fontSize: '0.875rem'
                    }}>
                      {user.total_logins}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                    {getStatusBadge(user)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Son Aktiviteler */}
      {activeView === 'activities' && (
        <div style={{ background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          {activities.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
              Henüz aktivite kaydı bulunmuyor
            </div>
          ) : (
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {activities.map((activity, index) => (
                <div 
                  key={activity.id || index} 
                  style={{ 
                    padding: '1rem', 
                    borderBottom: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    backgroundColor: activity.is_suspicious ? '#fef2f2' : 'white'
                  }}
                >
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: activity.is_suspicious ? '#fee2e2' : (activity.action === 'login' ? '#dcfce7' : '#fee2e2'),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {activity.is_suspicious ? (
                      <AlertTriangle size={20} color="#dc2626" />
                    ) : activity.action === 'login' ? (
                      <CheckCircle size={20} color="#16a34a" />
                    ) : (
                      <XCircle size={20} color="#dc2626" />
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500 }}>
                      {activity.user_name || 'Bilinmeyen Kullanıcı'}
                      <span style={{ fontWeight: 400, color: '#64748b', marginLeft: '0.5rem' }}>
                        {activity.action === 'login' ? 'giriş yaptı' : 'çıkış yaptı'}
                      </span>
                      {activity.is_suspicious && (
                        <span style={{ 
                          marginLeft: '0.5rem', 
                          background: '#fee2e2', 
                          color: '#dc2626', 
                          padding: '0.125rem 0.5rem', 
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 600
                        }}>
                          ⚠️ ŞÜPHELİ
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {activity.user_email}
                      {activity.ip_address && (
                        <>
                          <span>•</span>
                          <code style={{ 
                            background: activity.is_suspicious ? '#fee2e2' : '#f1f5f9', 
                            padding: '0.125rem 0.375rem', 
                            borderRadius: '4px',
                            fontSize: '0.75rem'
                          }}>
                            IP: {activity.ip_address}
                          </code>
                        </>
                      )}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', color: '#64748b', fontSize: '0.85rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <Calendar size={14} />
                      {formatDate(activity.created_at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <style>{`
        .spinning {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default UserActivitySettings;
